/* ============================================================================
   BRITTEN CUSTOM: Cart 4a bag PAGE view
   assets/britten-bag-page.js. Plain JavaScript, no Liquid.

   Loaded after assets/britten-bag.js (the core) by the Cart 4a branch of
   sections/cart__main.liquid. It owns everything on the bag page that touches the
   DOM: the section swap, focus, quantity and remove taps, the wedding date, the
   workshop note, the timing advice, the sticky checkout bar and the checkout lock.
   ========================================================================== */
(function (root) {
  'use strict';

  var bagCore = root.BrittenBagCoordinator;
  if (!bagCore || typeof root.document === 'undefined') { return; }
  var displayCount = bagCore.displayCount;
  var buildUrl = bagCore.buildUrl;
  var cartSignature = bagCore.cartSignature;
  var veilUnits = bagCore.veilUnits;
  var lockControls = bagCore.lockControls;
  var weddingAttributes = bagCore.weddingAttributes;
  var formatWeddingDate = bagCore.formatWeddingDate;
  var basketTiming = bagCore.basketTiming;
  var isWritableDate = bagCore.isWritableDate;
  var createUpdateWriter = bagCore.createUpdateWriter;
  var createEngine = bagCore.createEngine;


  /* ======================================================================
     View: the bag page. Everything below touches the DOM.
     ====================================================================== */

  var document = root.document;
  var UPDATING_DELAY_MS = 350;
  var VERIFY_MIN_GAP_MS = 15000;
  /* The veil limit's own constant (snippets/britten-veil-cart-limit.liquid). */
  var VEIL_LIMIT = 2;

  var section = null;
  var engine = null;
  var i18n = root.BrittenI18n || { t: function () { return ''; } };
  var updatingTimer = null;
  var lockObserver = null;
  var veilHold = false;
  var lastVerify = 0;
  var stickyObserver = null;
  var stickySizer = null;
  var rootUrlValue = '/';
  var originValue = '';
  var writer = null;
  var dateIntent;          /* the date as the customer last set it this page view (undefined = untouched) */
  var noteIntent;          /* the note as the customer last typed it */
  var WRITE_DELAY_MS = 600;
  var FLUSH_BOUND_MS = 1500;

  function readConfig() {
    var el = document.getElementById('britten-bag-config');
    if (!el) { return {}; }
    try { return JSON.parse(el.textContent || '{}') || {}; } catch (e) { return {}; }
  }

  function rootEl() { return section.querySelector('[data-britten-bag-root]'); }
  function statusEl() { return section.querySelector('[data-britten-bag-status]'); }
  function liveEl() { return section.querySelector('[data-britten-bag-live]'); }

  function groupEls() {
    return Array.prototype.slice.call(section.querySelectorAll('[data-britten-group]'));
  }

  function groupByKey(key) {
    var els = groupEls();
    for (var i = 0; i < els.length; i++) {
      if (els[i].getAttribute('data-britten-key') === key) { return els[i]; }
    }
    return null;
  }

  function titleOf(key) {
    var g = groupByKey(key);
    var t = g && g.querySelector('[data-britten-group-title]');
    return t ? t.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function groupKeys() {
    return groupEls().map(function (g) { return g.getAttribute('data-britten-key'); });
  }

  function announce(message) {
    var el = liveEl();
    if (!el || !message) { return; }
    /* Clear first so the same sentence twice still announces. */
    el.textContent = '';
    setTimeout(function () { el.textContent = message; }, 30);
  }

  /* ---- checkout lock ---- */

  /* The coordinator's lock is its own attribute so snippets/britten-veil-cart-
     limit.liquid (which un-gates by its own class) can never open a bag that is
     still being reconciled. Checkout is enabled only when BOTH are clear. */
  function setAttr(el, name, value) {
    if (el.getAttribute(name) !== value) { el.setAttribute(name, value); }
  }

  /* Checkout is held while the coordinator is busy or unresolved (locked), and
     also while the cart holds more wedding veils than the limit allows
     (veilHold). The second part covers the moment after a section swap, before
     the veil limit's own observer has put its class on the fresh buttons. */
  function applyLock(locked) {
    var held = locked || veilHold;
    lockControls(section, held);
    if (!locked) {
      var markers = section.querySelectorAll('[data-britten-bag-needs-check]');
      for (var i = 0; i < markers.length; i++) { markers[i].parentNode.removeChild(markers[i]); }
    }
  }

  function isHeld() {
    return engine.getState().locked || veilHold;
  }

  function setVeilHold(cart) {
    if (cart) { veilHold = veilUnits(cart) > VEIL_LIMIT; }
  }

  /* ---- status panel ---- */

  function paintStatus(s) {
    var el = statusEl();
    if (!el) { return; }
    var textEl = el.querySelector('[data-britten-bag-status-text]');
    var noteEl = el.querySelector('[data-britten-bag-status-note]');
    var actions = el.querySelector('[data-britten-bag-status-actions]');
    var retryBtn = el.querySelector('[data-britten-bag-retry]');
    if (s.error) {
      clearTimeout(updatingTimer);
      updatingTimer = null;
      el.removeAttribute('hidden');
      el.setAttribute('role', 'alert');
      textEl.textContent = i18n.t('js.bag.error');
      noteEl.textContent = '';
      actions.removeAttribute('hidden');
      if (retryBtn) { retryBtn.removeAttribute('hidden'); }
      return;
    }
    el.removeAttribute('role');
    actions.setAttribute('hidden', '');
    if (retryBtn) { retryBtn.setAttribute('hidden', ''); }
    if (s.locked) {
      if (!updatingTimer && el.hasAttribute('hidden')) {
        updatingTimer = setTimeout(function () {
          updatingTimer = null;
          var now = engine.getState();
          if (now.locked && !now.error) {
            textEl.textContent = i18n.t('js.bag.updating');
            noteEl.textContent = i18n.t('js.bag.updating_note');
            el.removeAttribute('hidden');
          }
        }, UPDATING_DELAY_MS);
      }
    } else {
      clearTimeout(updatingTimer);
      updatingTimer = null;
      el.setAttribute('hidden', '');
    }
  }

  /* Every quantity box reads the last CONFIRMED cart, not a wish. Runs whenever the engine is
     idle, so a failed or abandoned change cannot leave "3" on screen while the cart holds 2
     (the checkout form posts these boxes as updates[...]). A box being typed in is left alone. */
  function syncQuantities() {
    if (!engine) { return; }
    var cart = engine.confirmedCart();
    if (!cart) { return; }
    var inputs = section.querySelectorAll('[data-britten-qty-input]');
    var list = [];
    var byKey = {};
    for (var i = 0; i < inputs.length; i++) {
      var key = inputs[i].getAttribute('data-britten-key');
      byKey[key] = inputs[i];
      if (document.activeElement === inputs[i]) { continue; }
      list.push({ key: key, value: parseInt(inputs[i].value, 10) });
    }
    var fixes = bagCore.confirmedQuantities(list, cart, engine.intentFor);
    for (var j = 0; j < fixes.length; j++) { byKey[fixes[j].key].value = fixes[j].value; }
  }

  function onState(s) {
    if (s.pending === 0) { syncQuantities(); }
    applyLock(s.locked);
    var els = groupEls();
    for (var i = 0; i < els.length; i++) {
      var busy = !!s.busy[els[i].getAttribute('data-britten-key')];
      var cur = els[i].getAttribute('aria-busy');
      if (busy && cur !== 'true') { els[i].setAttribute('aria-busy', 'true'); }
      if (!busy && cur !== null) { els[i].removeAttribute('aria-busy'); }
    }
    var rootNode = rootEl();
    if (rootNode) {
      if (s.pending > 0) { setAttr(rootNode, 'aria-busy', 'true'); } else if (rootNode.hasAttribute('aria-busy')) { rootNode.removeAttribute('aria-busy'); }
    }
    paintStatus(s);
  }

  /* ---- section swap, focus, header count ---- */

  function focusSnapshot() {
    var a = document.activeElement;
    if (!a || !section.contains(a)) { return null; }
    var host = a.closest ? a.closest('[data-britten-focus]') : null;
    if (!host) { return null; }
    var group = host.closest('[data-britten-group]');
    var snap = { id: host.getAttribute('data-britten-focus'), index: -1 };
    if (group) { snap.index = groupEls().indexOf(group); }
    if (host.tagName === 'INPUT' && typeof host.selectionStart === 'number') {
      try { snap.start = host.selectionStart; snap.end = host.selectionEnd; } catch (e) { /* number inputs have no selection */ }
    }
    return snap;
  }

  function restoreFocus(snap) {
    if (!snap) { return; }
    var els = section.querySelectorAll('[data-britten-focus]');
    var i;
    for (i = 0; i < els.length; i++) {
      if (els[i].getAttribute('data-britten-focus') === snap.id) {
        els[i].focus({ preventScroll: true });
        return;
      }
    }
    /* The control is gone (a group was removed): move to the group that now
       sits where it was, else the empty-bag heading, else the page title. */
    var groups = groupEls();
    var target = null;
    if (groups.length) {
      var g = groups[Math.min(Math.max(snap.index, 0), groups.length - 1)];
      target = g.querySelector('.britten-bag-group__title a');
    }
    if (!target) { target = section.querySelector('.britten-cart-empty__title') || section.querySelector('.britten-bag__title'); }
    if (target) {
      if (!target.hasAttribute('tabindex') && target.tagName !== 'A') { target.setAttribute('tabindex', '-1'); }
      target.focus({ preventScroll: true });
    }
  }

  function updateHeaderCount(cart) {
    if (!cart) { return; }
    var count = displayCount(cart);
    var els = document.querySelectorAll('[data-bind="itemCount"]');
    for (var i = 0; i < els.length; i++) { els[i].textContent = count; }
    var trigger = document.querySelector('[data-ajax-cart-trigger]');
    if (trigger) {
      if (count > 0) { trigger.classList.add('has-cart-count'); } else { trigger.classList.remove('has-cart-count'); }
    }
  }

  function reinit() {
    /* The same hooks the old cart called after every swap. Only the ones whose
       snippet this page renders exist; the rest are absent and skipped. The
       wedding date, the notes and the sticky bar are now this file's own. */
    if (root._brittenCartDeliveryInit) { root._brittenCartDeliveryInit(); }
    initWeddingDate();
    observeSticky();
    refreshTiming();
  }

  /* ---- wedding date ---- */

  function core() { return root.BrittenCore && root.BrittenCore.prefs ? root.BrittenCore : null; }

  function savedDate() {
    var c = core();
    if (c) { return c.prefs.get('wedding_date'); }
    try { return root.localStorage.getItem('britten_wedding_date'); } catch (e) { return null; }
  }

  function saveDate(iso) {
    var c = core();
    if (c && c.prefs.set('wedding_date', iso || '') === true) { return; }
    try {
      if (iso) { root.localStorage.setItem('britten_wedding_date', iso); } else { root.localStorage.removeItem('britten_wedding_date'); }
    } catch (e) { /* storage unavailable */ }
  }

  /* The same write path as the old field (cart/update.js, keepalive so it can
     outlive a click straight on to checkout), now ONE serialised, debounced writer
     shared by the date and the note: the last value wins, requests never overlap. */
  function sendUpdate(body) {
    try {
      var p = root.fetch(buildUrl('/', 'cart/update.js', originValue), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        keepalive: true,
        body: JSON.stringify(body)
      });
      return (p && typeof p.then === 'function') ? p.then(function () {}, function () {}) : Promise.resolve();
    } catch (e) { return Promise.resolve(); }
  }

  function writeAttributes(iso) {
    if (!writer) { writer = createUpdateWriter(sendUpdate, WRITE_DELAY_MS); }
    writer.schedule({ attributes: weddingAttributes(iso) });
  }

  function writeNote(value) {
    if (!writer) { writer = createUpdateWriter(sendUpdate, WRITE_DELAY_MS); }
    writer.schedule({ note: value });
  }

  function wdInput() { return section.querySelector('[data-britten-bag-wd-input]'); }

  function initWeddingDate() {
    var input = wdInput();
    if (!input) { return; }
    var today = new Date();
    input.min = today.getFullYear() + '-' + ('0' + (today.getMonth() + 1)).slice(-2) + '-' + ('0' + today.getDate()).slice(-2);
    input.max = (today.getFullYear() + 3) + '-12-31';
    /* The customer's own choice this page view wins over whatever a section
       response carried (it may have been rendered before her last write landed:
       that is how a cleared date used to come back). */
    if (dateIntent !== undefined) {
      input.value = dateIntent;
      return;
    }
    var saved = savedDate();
    if (!input.value && saved) {
      input.value = saved;
      writeAttributes(saved);
    } else if (input.value && !saved) {
      saveDate(input.value);
    }
  }

  function onWeddingDateChange(input) {
    var iso = input.value || '';
    if (!iso) {
      /* Chrome reports '' while a date is half typed; only a real clear counts. */
      if (input.validity && input.validity.badInput) { return; }
      dateIntent = '';
      saveDate('');
      writeAttributes('');
      refreshTiming();
      return;
    }
    if (input.validity && input.validity.valid === false) { return; }
    if (!isWritableDate(iso, input.min, input.max)) { return; }
    dateIntent = iso;
    saveDate(iso);
    writeAttributes(iso);
    refreshTiming();
  }

  /* ---- workshop note ---- */

  function noteField() { return section.querySelector('[data-britten-note]'); }

  function showClear() {
    var ta = noteField();
    var btns = section.querySelectorAll('[data-britten-act="clear-note"]');
    for (var i = 0; i < btns.length; i++) {
      if (btns[i].hasAttribute('data-britten-note-preview-btn')) { continue; }
      if (ta) { if (ta.value) { btns[i].removeAttribute('hidden'); } else { btns[i].setAttribute('hidden', ''); } }
    }
  }

  function onNoteInput(ta) {
    noteIntent = ta.value;
    writeNote(ta.value);   /* exactly what she typed: no trimming, as the form posts it today */
    showClear();
  }

  /* "Clear note": empty the box, save note "" at once through the same serialised writer,
     say so, keep focus in the box, and hide the button. Also works from the empty-bag
     preview, where there is no box. */
  function clearNote() {
    noteIntent = '';
    var ta = noteField();
    if (ta) { ta.value = ''; ta.focus(); }
    writeNote('');
    writer.flush();
    var prev = section.querySelector('[data-britten-note-preview]');
    if (prev && prev.parentNode) { prev.parentNode.removeChild(prev); }
    showClear();
    announce(i18n.t('js.bag.announce_note_cleared'));
  }

  function noteSnapshot() {
    var ta = noteField();
    if (!ta) { return null; }
    var toggle = section.querySelector('[data-britten-act="notes"]');
    var snap = { expanded: !!(toggle && toggle.getAttribute('aria-expanded') === 'true'), focus: document.activeElement === ta };
    if (snap.focus) { try { snap.start = ta.selectionStart; snap.end = ta.selectionEnd; } catch (e) { /* no selection */ } }
    return snap;
  }

  function restoreNote(snap) {
    var ta = noteField();
    if (!ta || !snap) { return; }
    if (noteIntent !== undefined) { ta.value = noteIntent; }
    showClear();
    var toggle = section.querySelector('[data-britten-act="notes"]');
    var field = section.querySelector('#britten-bag-notes-field');
    if (snap.expanded || ta.value) {
      if (toggle) { toggle.setAttribute('aria-expanded', 'true'); }
      if (field) { field.removeAttribute('hidden'); }
    }
    if (snap.focus) {
      ta.focus({ preventScroll: true });
      try { if (typeof snap.start === 'number') { ta.setSelectionRange(snap.start, snap.end); } } catch (e) { /* no selection */ }
    }
  }

  /* Checkout starts: send any date or note still waiting, then go on. Bounded, so
     a slow network can never trap her on the page. */
  function flushThenCheckout(btn) {
    var done = false;
    function go() {
      if (done) { return; }
      done = true;
      var form = document.getElementById('cart_form');
      if (form && typeof form.requestSubmit === 'function') { form.requestSubmit(btn); } else { btn.click(); }
    }
    var bound = setTimeout(go, FLUSH_BOUND_MS);
    writer.flush().then(function () { clearTimeout(bound); go(); });
  }

  /* ---- timing advice ---- */

  function primaryLines() {
    return groupEls().map(function (g) {
      var tags = (g.getAttribute('data-britten-tags') || '').split(',').filter(function (t) { return t; });
      var t = g.querySelector('[data-britten-group-title]');
      return { title: t ? t.textContent.replace(/\s+/g, ' ').trim() : '', tags: tags };
    });
  }

  function refreshTiming() {
    var el = section.querySelector('[data-britten-bag-timing]');
    var input = wdInput();
    if (!el || !input) { return; }
    var iso = input.value || '';
    var calc = root.BrittenDeliveryCalc;
    var verdict = basketTiming(primaryLines(), iso, calc, root.brittenDeliveryCountry || undefined);
    var vars = verdict.vars;
    vars.date = formatWeddingDate(iso, root.BrittenI18n);
    var message = '';
    var ask = false;
    if (verdict.state === 'good') { message = i18n.t('js.bag.timing_good'); }
    else if (verdict.state === 'tight') { message = i18n.t('js.bag.timing_tight', { service: vars.service, dates: vars.dates }); }
    else if (verdict.state === 'slow_item') { message = i18n.t('js.bag.timing_slow_item', { date: vars.date, product: vars.product, dates: vars.dates }); ask = true; }
    else if (verdict.state === 'late') { message = i18n.t('js.bag.timing_late', { date: vars.date, dates: vars.dates }); ask = true; }
    else if (verdict.state === 'unknown') { message = i18n.t('js.bag.timing_unknown'); ask = true; }
    el.setAttribute('data-state', verdict.state);
    el.textContent = message;
    if (ask && message) {
      var host = section.querySelector('[data-britten-bag-contact]');
      var url = host ? host.getAttribute('data-britten-bag-contact') : '';
      if (url) {
        var a = document.createElement('a');
        a.href = url;
        a.className = 'britten-bag-link';
        a.textContent = i18n.t('js.bag.timing_ask');
        el.appendChild(document.createTextNode(' '));
        el.appendChild(a);
      }
    }
    if (message) { el.removeAttribute('hidden'); } else { el.setAttribute('hidden', ''); }
  }

  /* ---- sticky checkout bar ---- */

  /* At 1024px and up the bar shows only while the panel's own checkout button
     is off screen. Below that it is a footer bar, always shown (CSS). The page
     bottom padding follows the bar's real height, so wrapped translations never
     cover the last control. */
  function observeSticky() {
    if (stickyObserver) { stickyObserver.disconnect(); stickyObserver = null; }
    if (stickySizer) { stickySizer.disconnect(); stickySizer = null; }
    var bar = section.querySelector('[data-britten-bag-sticky]');
    var btn = section.querySelector('[data-britten-bag-checkout]');
    if (!bar) { section.style.removeProperty('--britten-bag-sticky-h'); return; }
    if (btn && typeof root.IntersectionObserver !== 'undefined') {
      stickyObserver = new root.IntersectionObserver(function (entries) {
        var visible = entries[entries.length - 1].isIntersecting;
        if (visible) { bar.removeAttribute('data-britten-bag-sticky-on'); } else { bar.setAttribute('data-britten-bag-sticky-on', ''); }
        sizeSticky(bar);
      }, { threshold: 0.01 });
      stickyObserver.observe(btn);
    } else {
      bar.setAttribute('data-britten-bag-sticky-on', '');
    }
    if (typeof root.ResizeObserver !== 'undefined') {
      stickySizer = new root.ResizeObserver(function () { sizeSticky(bar); });
      stickySizer.observe(bar);
    }
    sizeSticky(bar);
  }

  function sizeSticky(bar) {
    var shown = bar.offsetHeight > 0 && root.getComputedStyle(bar).display !== 'none';
    section.style.setProperty('--britten-bag-sticky-h', shown ? bar.offsetHeight + 'px' : '0px');
  }

  function swapSection(html, cart) {
    var doc;
    try { doc = new root.DOMParser().parseFromString(html, 'text/html'); } catch (e) { doc = null; }
    var fresh = doc && doc.querySelector('[data-britten-bag-root]');
    var live = rootEl();
    if (!fresh || !live) {
      var err = new Error('render');
      err.code = 'render';
      throw err;
    }
    var snap = focusSnapshot();
    var noteSnap = noteSnapshot();
    live.innerHTML = fresh.innerHTML;
    reinit();
    restoreNote(noteSnap);
    setVeilHold(cart);
    applyLock(engine.getState().locked);
    onState(engine.getState());
    updateHeaderCount(cart);
    reapplyIntent();
    restoreFocus(snap);
  }

  /* A swap shows the cart as the server has it. Taps still queued have not
     landed, so put the customer's latest wish back in the boxes. */
  function reapplyIntent() {
    var inputs = section.querySelectorAll('[data-britten-qty-input]');
    for (var i = 0; i < inputs.length; i++) {
      var want = engine.intentFor(inputs[i].getAttribute('data-britten-key'));
      if (want !== undefined) { inputs[i].value = want; }
    }
  }

  /* What is on screen, as the same "key:quantity" pairs cartSignature makes. */
  function domSignature() {
    var inputs = section.querySelectorAll('input[name^="updates["]');
    var out = [];
    for (var i = 0; i < inputs.length; i++) {
      var name = inputs[i].getAttribute('name');
      out.push(name.slice(8, -1) + ':' + parseInt(inputs[i].defaultValue, 10));
    }
    return out.sort().join('|');
  }

  /* ---- events ---- */

  function quantityInputFor(groupEl) {
    return groupEl ? groupEl.querySelector('[data-britten-qty-input]') : null;
  }

  function announceFor(res, kind, title, before) {
    if (!res || !res.ok) { return; }
    if (kind === 'remove') { announce(i18n.t('js.bag.announce_removed', { product: title })); return; }
    if (kind === 'refresh') {
      var now = groupKeys();
      for (var i = 0; i < now.length; i++) {
        if (before.indexOf(now[i]) === -1) {
          announce(i18n.t('js.bag.announce_added', { product: titleOf(now[i]) }));
          return;
        }
      }
    }
    announce(i18n.t('js.bag.announce_updated'));
  }

  function changeQuantity(key, next) {
    var title = titleOf(key);
    var removing = next <= 0;
    var p = engine.setQuantity(key, next);
    p.then(function (res) {
      announceFor(res, removing ? 'remove' : 'update', title);
    });
  }

  function commitInput(input) {
    var key = input.getAttribute('data-britten-key');
    var n = parseInt(input.value, 10);
    if (isNaN(n) || n < 0) { n = 0; }
    input.value = n;
    changeQuantity(key, n);
  }

  function onClick(e) {
    var checkout = e.target.closest ? e.target.closest('button[name="checkout"]') : null;
    if (checkout && isHeld()) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (checkout && writer && writer.hasWork()) {
      e.preventDefault();
      e.stopPropagation();
      flushThenCheckout(checkout);
      return;
    }
    var el = e.target.closest ? e.target.closest('[data-britten-act]') : null;
    if (!el || !section.contains(el)) { return; }
    var act = el.getAttribute('data-britten-act');
    var key = el.getAttribute('data-britten-key');
    var groupEl = key ? groupByKey(key) : null;
    var input = quantityInputFor(groupEl);
    var current;
    if (act === 'inc' || act === 'dec') {
      e.preventDefault();
      if (!input) { return; }
      var wanted = engine.intentFor(key);
      current = wanted !== undefined ? wanted : (parseInt(input.value, 10) || 0);
      var next = act === 'inc' ? current + 1 : Math.max(0, current - 1);
      input.value = next;
      changeQuantity(key, next);
    } else if (act === 'remove') {
      e.preventDefault();
      var title = titleOf(key);
      engine.removeGroup(key).then(function (res) { announceFor(res, 'remove', title); });
    } else if (act === 'retry') {
      e.preventDefault();
      engine.retry().then(function (res) { announceFor(res, 'update'); });
    } else if (act === 'clear-note') {
      e.preventDefault();
      clearNote();
    } else if (act === 'notes') {
      e.preventDefault();
      var field = section.querySelector('#britten-bag-notes-field');
      var open = el.getAttribute('aria-expanded') === 'true';
      el.setAttribute('aria-expanded', open ? 'false' : 'true');
      if (field) {
        if (open) { field.setAttribute('hidden', ''); } else {
          field.removeAttribute('hidden');
          var ta = field.querySelector('textarea');
          if (ta) { ta.focus(); }
        }
      }
    } else if (act === 'add') {
      e.preventDefault();
      var variant = parseInt(el.getAttribute('data-britten-variant'), 10);
      if (!variant) { return; }
      el.disabled = true;
      el.textContent = i18n.t('js.cart.upsell_adding');
      engine.addItem(variant, 1).then(function (res) {
        if (!res.ok && el.isConnected !== false) {
          el.disabled = false;
          el.textContent = i18n.t('js.cart.upsell_retry');
        }
        announceFor(res, 'update');
      });
    }
  }

  function onChange(e) {
    var t = e.target;
    if (!t || !t.hasAttribute) { return; }
    if (t.hasAttribute('data-britten-qty-input')) { commitInput(t); }
    else if (t.hasAttribute('data-britten-bag-wd-input')) { onWeddingDateChange(t); }
  }

  function onKeydown(e) {
    var t = e.target;
    /* Enter in a quantity box would submit the form, which is checkout. Commit
       the number instead. */
    if (e.key === 'Enter' && t && t.hasAttribute && t.hasAttribute('data-britten-qty-input')) {
      e.preventDefault();
      commitInput(t);
    }
  }

  function onSubmit(e) {
    /* Only the bag's own checkout form. Any other form on the page is not ours to hold. */
    if (e.target && e.target.id !== 'cart_form') { return; }
    if (isHeld()) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  function onFailure(err, meta) {
    if (meta && meta.key) {
      var input = quantityInputFor(groupByKey(meta.key));
      if (input) { input.value = input.defaultValue; }
    }
  }

  function onSettled(info) {
    if (info.cart) {
      setVeilHold(info.cart);
      updateHeaderCount(info.cart);
    }
    if (info.mutated || (info.meta && info.meta.kind === 'refresh')) {
      try {
        document.dispatchEvent(new root.CustomEvent('britten:cart:updated', {
          bubbles: true,
          detail: { source: 'britten-bag', cart: info.cart }
        }));
      } catch (e) { /* old browsers: no event */ }
    }
  }

  /* ---- boot ---- */

  function init() {
    section = document.querySelector('[data-britten-bag]');
    if (!section) { return; }
    var cfg = readConfig();
    var shopifyRoot = root.Shopify && root.Shopify.routes && root.Shopify.routes.root_url;
    rootUrlValue = shopifyRoot || cfg.rootUrl || '/';
    originValue = root.location ? root.location.origin : '';

    engine = createEngine({
      fetch: function (url, init) { return root.fetch(url, init); },
      rootUrl: shopifyRoot || cfg.rootUrl || '/',
      sectionId: cfg.sectionId || 'cart__main',
      sectionsUrl: root.location ? root.location.pathname : '',
      origin: root.location ? root.location.origin : '',
      hooks: {
        onState: onState,
        render: swapSection,
        onSettled: onSettled,
        onFailure: onFailure,
        signature: domSignature
      }
    });
    /* The veil limit seeds its own count from Liquid; start from the same fact. */
    if (typeof root.__brittenVeilLimitInitialCount === 'number') {
      veilHold = root.__brittenVeilLimitInitialCount > VEIL_LIMIT;
    }

    section.addEventListener('click', onClick, true);
    section.addEventListener('change', onChange);
    section.addEventListener('input', function (e) {
      if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-britten-note')) { onNoteInput(e.target); }
    });
    section.addEventListener('focusout', function (e) {
      if (e.target && e.target.hasAttribute && e.target.hasAttribute('data-britten-note') && writer) { writer.flush(); }
    });
    section.addEventListener('keydown', onKeydown);
    section.addEventListener('submit', onSubmit, true);

    /* While a note or the date is being typed the on-screen keyboard would sit
       on the footer bar: step the bar aside, and bring it back after. The panel
       keeps its own checkout button in the page. */
    function setStickyAside(aside) {
      var bar = section.querySelector('[data-britten-bag-sticky]');
      if (!bar) { return; }
      if (aside) { bar.setAttribute('data-britten-bag-sticky-aside', ''); } else { bar.removeAttribute('data-britten-bag-sticky-aside'); }
    }
    section.addEventListener('focusin', function (e) {
      if (e.target && e.target.matches && e.target.matches('textarea, input[type="date"]')) { setStickyAside(true); }
    });
    section.addEventListener('focusout', function () {
      setTimeout(function () { setStickyAside(false); }, 300);
    });

    /* Another script (the veil limit) may remove disabled/inert from a control
       it gated earlier. While the coordinator holds the lock, put it back. */
    if (typeof root.MutationObserver !== 'undefined') {
      lockObserver = new root.MutationObserver(function () {
        if (isHeld()) { applyLock(true); }
      });
      lockObserver.observe(section, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['disabled', 'inert', 'data-britten-bag-locked']
      });
    }

    /* Same signatures as the old cart: other scripts call these. */
    root._brittenRefreshCartSection = function (callback) {
      var before = groupKeys();
      return engine.refresh().then(function (res) {
        announceFor(res, 'refresh', '', before);
        if (res.ok && typeof callback === 'function') { callback(); }
        return res;
      });
    };
    root.brittenCartReinit = reinit;

    /* A date changed elsewhere (the product page, Your Britten): show it and
       write the cart attributes, as the field does. Never fires for this
       field's own write. */
    document.addEventListener('britten:prefs:wedding_date', function (e) {
      var v = e && e.detail && e.detail.value;
      var input = wdInput();
      if (!input || (v || '') === input.value) { return; }
      dateIntent = v || '';
      input.value = v || '';
      writeAttributes(v || '');
      refreshTiming();
    });

    /* Back/forward cache and a tab left open: the page can show an old bag.
       Read the cart; if it differs from the screen, re-render and re-lock. */
    root.addEventListener('pageshow', function (e) {
      if (e.persisted) { lastVerify = Date.now(); engine.verify(); }
    });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'visible') { return; }
      if (Date.now() - lastVerify < VERIFY_MIN_GAP_MS) { return; }
      lastVerify = Date.now();
      engine.verify();
    });

    /* The script has taken over: drop the plain note Liquid printed for a bag
       it saw needed checking, so the live status panel is the only voice. */
    var statusNode = statusEl();
    if (statusNode) {
      statusNode.setAttribute('hidden', '');
      var t1 = statusNode.querySelector('[data-britten-bag-status-text]');
      var t2 = statusNode.querySelector('[data-britten-bag-status-note]');
      var act = statusNode.querySelector('[data-britten-bag-status-actions]');
      if (t1) { t1.textContent = ''; }
      if (t2) { t2.textContent = ''; }
      if (act) { act.setAttribute('hidden', ''); }
    }

    onState(engine.getState());
    initWeddingDate();
    observeSticky();
    refreshTiming();
    /* The delivery maths is defined by a script that may load after this one. */
    root.addEventListener('load', refreshTiming);
    engine.start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
